// ABOUTME: Date formatting utilities for consistent date/time display across the application
// ABOUTME: Handles UTC to local time conversion and provides user-friendly date formats

import { parseISO, formatDistanceToNow } from 'date-fns';

/**
 * Convert a UTC Date to a "fake local" Date that displays in the family timezone.
 * React-big-calendar positions events based on Date.getHours() (browser local time).
 * This function creates a Date where getHours() == the wall clock hour in familyTimezone,
 * ensuring events display at the correct time regardless of browser timezone.
 */
export const utcToFamilyTimezoneDisplay = (utcDate: Date, familyTimezone: string): Date => {
  const dateStr = utcDate.toLocaleDateString('en-CA', { timeZone: familyTimezone }); // YYYY-MM-DD
  const timeStr = utcDate.toLocaleTimeString('en-GB', {
    timeZone: familyTimezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }); // HH:MM:SS
  // No timezone offset: JS treats this as browser local time, so getHours() == family tz hours
  return new Date(`${dateStr}T${timeStr}`);
};

/**
 * Get current date string in family timezone (YYYY-MM-DD format)
 * Family timezone should always be set in DB - browser fallback is defensive only
 */
export const getTodayInFamilyTimezone = (familyTimezone?: string): string => {
  const now = new Date();
  if (familyTimezone) {
    return now.toLocaleDateString('en-CA', { timeZone: familyTimezone });
  }
  return now.toLocaleDateString('en-CA'); // Defensive fallback to browser timezone
};

/**
 * Convert a date to family timezone date string (YYYY-MM-DD format)
 * Family timezone should always be set in DB - browser fallback is defensive only
 */
export const getDateInFamilyTimezone = (date: Date, familyTimezone?: string): string => {
  if (familyTimezone) {
    return date.toLocaleDateString('en-CA', { timeZone: familyTimezone });
  }
  return date.toLocaleDateString('en-CA'); // Defensive fallback to browser timezone
};

/**
 * True if a goal's endDate falls on the family's current calendar day (or later),
 * comparing pure date strings so neither side needs Date-object round-tripping.
 */
export const isGoalEndingToday = (endDate: string | null | undefined, familyTimezone?: string): boolean => {
  if (!endDate) return false;
  const endDateStr = getDateInFamilyTimezone(new Date(endDate), familyTimezone);
  const todayStr = getTodayInFamilyTimezone(familyTimezone);
  return endDateStr >= todayStr;
};

/**
 * Get today's date for behavior tracking, considering 3am daily reset pattern
 * If it's before 3am in family timezone, returns previous calendar day
 * This aligns with the app's daily reset behavior at 3am
 */
export const getTodayForBehaviors = (familyTimezone?: string): string => {
  const now = new Date();

  // If timezone provided, check 3am reset in family timezone
  if (familyTimezone) {
    // Format straight to hour/date strings — no round-trip through a bare
    // new Date(toLocaleString(...)), which would reinterpret the formatted
    // wall-clock time in the browser's own timezone.
    // % 24 guards against ICU implementations that format midnight as "24" rather than "00".
    const hour = parseInt(
      now.toLocaleTimeString('en-GB', { timeZone: familyTimezone, hour: '2-digit', hour12: false }),
      10
    ) % 24;
    const dateStr = now.toLocaleDateString('en-CA', { timeZone: familyTimezone });

    // If before 3am in family timezone, use previous day
    if (hour < 3) {
      const [y, m, d] = dateStr.split('-').map(Number);
      const previousDay = new Date(Date.UTC(y, m - 1, d - 1));
      return `${previousDay.getUTCFullYear()}-${String(previousDay.getUTCMonth() + 1).padStart(2, '0')}-${String(previousDay.getUTCDate()).padStart(2, '0')}`;
    }

    return dateStr;
  }

  // Defensive fallback - family timezone should always be set in DB
  let dayForBehaviors = new Date(now);
  if (now.getHours() < 3) {
    dayForBehaviors.setDate(dayForBehaviors.getDate() - 1);
  }

  return dayForBehaviors.toLocaleDateString('en-CA'); // Browser timezone fallback
};

/**
 * Get today's date in family timezone (midnight to midnight)
 * This is for UI display purposes, not behavioral tracking
 */
export const getTodayInFamilyTimezoneForDisplay = (familyTimezone?: string): string => {
  return getTodayInFamilyTimezone(familyTimezone);
};

export const formatUTCDateTime = (utcDateString: string): { date: string; time: string } | null => {
  if (!utcDateString) {
    return null;
  }

  try {
    const date = new Date(utcDateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return null;
    }

    // Format date (e.g., "Jan 15, 2024")
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    // Format time (e.g., "2:30 PM")
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });

    return {
      date: formattedDate,
      time: formattedTime
    };
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
};

// UNIFIED TIME WINDOW SYSTEM for analytics cards (issues #726-739)
// See /UNIFIED_TIME_WINDOW_SYSTEM.md for full implementation guidance
export interface TimeWindowOption {
  type: 'preset' | 'custom';
  value?: string;
  startDate?: string;
  endDate?: string;
}

export interface DateRange {
  startDate: string; // YYYY-MM-DD format
  endDate: string;   // YYYY-MM-DD format
}

/**
 * Subtract N days from a YYYY-MM-DD date string, returning another YYYY-MM-DD string.
 * Uses Date.UTC + getUTC* accessors — pure calendar-day arithmetic that never touches
 * the browser's local timezone, so it can't shift the day the way constructing a Date
 * via the browser-local multi-arg constructor (or subtracting via local setDate/getDate)
 * would. `dateStr` is assumed to already be correct for whatever timezone matters
 * (typically family timezone) — no further timezone conversion happens here.
 */
export const daysBackFrom = (dateStr: string, days: number): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const anchor = new Date(Date.UTC(year, month - 1, day - days));
  return `${anchor.getUTCFullYear()}-${String(anchor.getUTCMonth() + 1).padStart(2, '0')}-${String(anchor.getUTCDate()).padStart(2, '0')}`;
};

/**
 * Convert time window selection to concrete date range
 * Unified utility for all analytics cards
 */
export const getDateRangeFromTimeWindow = (
  timeWindow?: string | TimeWindowOption, 
  familyTimezone?: string
): DateRange => {
  const today = getTodayInFamilyTimezone(familyTimezone);
  
  if (typeof timeWindow === 'string') {
    switch (timeWindow) {
      case 'Today':
        return { startDate: today, endDate: today };
      
      case 'Last 7 days':
        return { startDate: daysBackFrom(today, 6), endDate: today }; // 6 days ago + today = 7 days

      case 'Last 30 days':
        return { startDate: daysBackFrom(today, 29), endDate: today }; // 29 days ago + today = 30 days

      case 'Last 90 days':
        return { startDate: daysBackFrom(today, 89), endDate: today }; // 89 days ago + today = 90 days
      
      default:
        // Default to today
        return { startDate: today, endDate: today };
    }
  } else if (timeWindow?.type === 'custom' && timeWindow.startDate && timeWindow.endDate) {
    return { 
      startDate: timeWindow.startDate, 
      endDate: timeWindow.endDate 
    };
  }
  
  // Default to today
  return { startDate: today, endDate: today };
};

/**
 * Get this week's date range (Sunday to Saturday) in family timezone
 * Used for weekly behavior calculations that respect family timezone
 */
export const getThisWeekDateRange = (familyTimezone?: string): DateRange => {
  const today = getTodayInFamilyTimezone(familyTimezone);
  const [year, month, day] = today.split('-').map(Number);
  // Pure calendar-day arithmetic via Date.UTC + getUTC* — never touches the browser's
  // local timezone, so it can't shift the week the way constructing a Date via the
  // browser-local constructor (then reconverting through familyTimezone) can.
  const todayUtc = new Date(Date.UTC(year, month - 1, day));
  const daysToSubtract = todayUtc.getUTCDay(); // 0 = Sunday, 1 = Monday, etc.

  const startOfWeek = new Date(Date.UTC(year, month - 1, day - daysToSubtract));
  const endOfWeek = new Date(Date.UTC(year, month - 1, day - daysToSubtract + 6));

  const toDateStr = (d: Date) =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

  return {
    startDate: toDateStr(startOfWeek),
    endDate: toDateStr(endOfWeek)
  };
};

export type InsightsGranularity = 'day' | 'week' | 'month';

export interface InsightsDateRange {
  startDate: string;  // YYYY-MM-DD
  endDate: string;    // YYYY-MM-DD
  granularity: InsightsGranularity;
}

/**
 * Compute date range and granularity for FamilyInsightsPage time horizon tabs.
 * All windows are rolling (not calendar-anchored).
 */
export const getInsightsDateRange = (
  tabLabel: string,
  familyTimezone: string | undefined,
  familyCreatedAt: string | undefined
): InsightsDateRange => {
  const today = getTodayInFamilyTimezone(familyTimezone);
  const todayDate = new Date(today + 'T00:00:00');

  switch (tabLabel) {
    case 'Week': {
      const start = new Date(todayDate);
      start.setDate(start.getDate() - 6);
      return {
        startDate: getDateInFamilyTimezone(start, familyTimezone),
        endDate: today,
        granularity: 'day',
      };
    }
    case 'Month': {
      const start = new Date(todayDate);
      start.setDate(start.getDate() - 29);
      return {
        startDate: getDateInFamilyTimezone(start, familyTimezone),
        endDate: today,
        granularity: 'day',
      };
    }
    case '3 Months': {
      const start = new Date(todayDate);
      start.setDate(start.getDate() - 89);
      return {
        startDate: getDateInFamilyTimezone(start, familyTimezone),
        endDate: today,
        granularity: 'week',
      };
    }
    case '6 Months': {
      const start = new Date(todayDate);
      start.setDate(start.getDate() - 179);
      return {
        startDate: getDateInFamilyTimezone(start, familyTimezone),
        endDate: today,
        granularity: 'week',
      };
    }
    case 'All Time':
    default: {
      const start = familyCreatedAt
        ? new Date(familyCreatedAt)
        : (() => { const d = new Date(todayDate); d.setDate(d.getDate() - 364); return d; })();
      return {
        startDate: getDateInFamilyTimezone(start, familyTimezone),
        endDate: today,
        granularity: 'month',
      };
    }
  }
};

/**
 * Get display title for time window
 * Unified utility for all analytics cards
 */
export const getTimeWindowDisplayTitle = (
  timeWindow?: string | TimeWindowOption,
  baseTitle: string = "Behaviors"
): string => {
  if (typeof timeWindow === 'string') {
    switch (timeWindow) {
      case 'Today':
        return `Today's ${baseTitle}`;
      case 'Last 7 days':
        return `Last 7 Days: ${baseTitle}`;
      case 'Last 30 days':
        return `Last 30 Days: ${baseTitle}`;
      case 'Last 90 days':
        return `Last 90 Days: ${baseTitle}`;
      default:
        return `Today's ${baseTitle}`;
    }
  } else if (timeWindow?.type === 'custom' && timeWindow.startDate && timeWindow.endDate) {
    return `${timeWindow.startDate} to ${timeWindow.endDate}: ${baseTitle}`;
  }

  return `Today's ${baseTitle}`;
};

/**
 * Convert month name to number (1-12)
 * Used by both FamilySetupWizard and AdminFamilyDetail for consistent month handling
 */
export const monthNameToNumber = (monthName: string): number => {
  const monthMap: { [key: string]: number } = {
    'January': 1, 'February': 2, 'March': 3, 'April': 4,
    'May': 5, 'June': 6, 'July': 7, 'August': 8,
    'September': 9, 'October': 10, 'November': 11, 'December': 12
  };
  return monthMap[monthName] || 1; // Fallback to January for invalid month names
};

/**
 * Convert month number (1-12) to month name
 * Used by MyFamilyPage for consistent month display when editing existing children
 */
export const monthNumberToName = (monthNumber: number): string => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];
  return monthNames[monthNumber - 1] || 'January'; // Fallback to January for invalid month numbers
};

/**
 * Calculate age from birth date
 * Used by AdminFamilyDetail and other child-related components
 */
export const calculateAge = (birthDate: string): number | string => {
  try {
    const birth = new Date(birthDate);
    const today = new Date();
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1;
    }
    return age;
  } catch {
    return 'Unknown';
  }
};

/**
 * Format date string to locale date string
 * Simple wrapper for consistent date display
 */
export const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return 'Invalid date';
  }
};

/**
 * Formats a 'YYYY-MM-DD' date-only string as "Jun 12" (e.g. a goal's "started" label).
 * Parses the parts directly rather than `new Date(dateOnlyString)`, which parses as UTC
 * midnight and can shift a day in negative-offset timezones (issue #1900).
 */
export const formatShortDate = (dateOnly: string): string => {
  const [year, month, day] = dateOnly.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Format date string as relative time (e.g., "2 hours ago")
 * Uses date-fns formatDistanceToNow, handles UTC timestamps correctly
 */
export const formatRelativeTime = (dateString?: string): string => {
  if (!dateString) return 'No activity';
  try {
    // parseISO correctly handles UTC timestamps with 'Z' suffix
    // This ensures consistent elapsed time regardless of viewer timezone
    const utcDate = parseISO(dateString);
    return formatDistanceToNow(utcDate, { addSuffix: true });
  } catch {
    return 'Invalid date';
  }
};